// Client mirror of functions-common/src/charts/chartSpecSchema.ts. Keep in sync.

import type {
  ChartMeasureAgg,
  ChartSpec,
  ChartSpecError,
  ChartSpecErrorCode,
  ChartType,
  DatasetKey,
  ValidationResult,
} from './types.js'
import {
  CHART_MAX_AXIS_LABEL_CHARS,
  CHART_MAX_COLORS,
  CHART_MAX_FILTERS,
  CHART_MAX_FILTER_IN_ITEMS,
  CHART_MAX_RELATIVE_DAYS,
  CHART_MAX_RELATIVE_MONTHS,
  CHART_MAX_RELATIVE_WEEKS,
  CHART_MAX_SERIES_COLOR_KEYS,
  CHART_MAX_STRING_ID_CHARS,
  CHART_MAX_SUBTITLE_CHARS,
  CHART_MAX_TITLE_CHARS,
  CHART_MIN_TOP_N,
  CHART_MAX_TOP_N,
  CHART_SPEC_MAX_JSON_CHARS,
} from './limits.js'
import { DATASET_CATALOG } from './datasets/index.js'

const VALID_CHART_TYPES = new Set<ChartType>([
  'bar',
  'line',
  'area',
  'donut',
  'number',
  'table',
  'gauge',
  'progress',
  'burn',
  'targetActual',
  'combo',
])

const VALID_MEASURE_AGGS = new Set<ChartMeasureAgg>([
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'distinct',
])

const VALID_TIME_BUCKETS = new Set(['day', 'week', 'month', 'quarter'])
const VALID_FILTER_OPS = new Set(['is', 'isNot', 'in', 'gt', 'lt', 'between', 'isEmpty'])
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/
const DATE_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/

const ALLOWED_SPEC_KEYS = new Set([
  'version',
  'id',
  'workspaceId',
  'title',
  'subtitle',
  'dataset',
  'measure',
  'secondMeasure',
  'breakdown',
  'time',
  'range',
  'compare',
  'filters',
  'target',
  'type',
  'style',
  'createdBy',
  'createdAt',
  'updatedBy',
  'updatedAt',
  'copiedFrom',
  'fromKit',
])

const ALLOWED_STYLE_KEYS = new Set([
  'palette',
  'colors',
  'seriesColor',
  'legend',
  'dataLabels',
  'gridlines',
  'axisLabels',
  'sort',
  'goalLine',
  'orientation',
  'stacking',
  'curve',
  'lineStyle',
  'innerRadius',
  'burn',
  'numberFormat',
])

const VALID_LEGENDS = new Set(['auto', 'top', 'right', 'bottom', 'off'])
const VALID_SORTS = new Set(['value-desc', 'value-asc', 'label'])
const VALID_NUMBER_FORMATS = new Set(['auto', 'integer', 'percent', 'currency', 'duration'])

function isScalar(val: unknown): boolean {
  if (val === null) return true
  const t = typeof val
  if (t === 'string' || t === 'boolean') return true
  if (t === 'number') return !Number.isNaN(val)
  return false
}

function isValidIsoDateOrNumber(val: unknown): boolean {
  if (typeof val === 'number') return !Number.isNaN(val)
  if (typeof val === 'string') {
    const parsed = Date.parse(val)
    return !Number.isNaN(parsed)
  }
  return false
}

export function collectChartSpecErrors(input: unknown): ChartSpecError[] {
  const errors: ChartSpecError[] = []

  let jsonStr: string
  try {
    jsonStr = JSON.stringify(input)
  } catch {
    errors.push({ code: 'spec.invalid', path: '', message: 'Spec cannot be serialized to JSON.' })
    return errors
  }

  if (jsonStr.length > CHART_SPEC_MAX_JSON_CHARS) {
    errors.push({
      code: 'spec.tooLarge',
      path: '',
      message: `Spec JSON exceeds maximum length of ${CHART_SPEC_MAX_JSON_CHARS} characters.`,
    })
  }

  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    errors.push({ code: 'spec.invalid', path: '', message: 'Spec must be a non-null object.' })
    return errors
  }

  const candidate = input as Record<string, unknown>

  // Unknown top-level keys check
  for (const key of Object.keys(candidate)) {
    if (!ALLOWED_SPEC_KEYS.has(key)) {
      errors.push({
        code: 'spec.unknownKey',
        path: key,
        message: `Unknown top-level property "${key}" in chart spec.`,
      })
    }
  }

  if (candidate.version !== 1) {
    errors.push({ code: 'spec.versionInvalid', path: 'version', message: 'Spec version must be 1.' })
  }

  if (typeof candidate.id !== 'string' || !candidate.id.trim()) {
    errors.push({ code: 'spec.idInvalid', path: 'id', message: 'Spec id must be a non-empty string.' })
  } else if (candidate.id.length > CHART_MAX_STRING_ID_CHARS) {
    errors.push({
      code: 'string.tooLong',
      path: 'id',
      message: `Spec id exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
    })
  }

  if (typeof candidate.workspaceId !== 'string' || !candidate.workspaceId.trim()) {
    errors.push({
      code: 'spec.workspaceIdInvalid',
      path: 'workspaceId',
      message: 'Spec workspaceId must be a non-empty string.',
    })
  } else if (candidate.workspaceId.length > CHART_MAX_STRING_ID_CHARS) {
    errors.push({
      code: 'string.tooLong',
      path: 'workspaceId',
      message: `Spec workspaceId exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
    })
  }

  if (typeof candidate.title !== 'string') {
    errors.push({ code: 'title.missing', path: 'title', message: 'Spec title must be a string.' })
  } else {
    if (!candidate.title.trim()) {
      errors.push({ code: 'title.empty', path: 'title', message: 'Spec title cannot be empty.' })
    }
    if (candidate.title.length > CHART_MAX_TITLE_CHARS) {
      errors.push({
        code: 'title.tooLong',
        path: 'title',
        message: `Title must be at most ${CHART_MAX_TITLE_CHARS} characters (got ${candidate.title.length}).`,
      })
    }
  }

  if (candidate.subtitle != null) {
    if (typeof candidate.subtitle !== 'string') {
      errors.push({
        code: 'subtitle.tooLong',
        path: 'subtitle',
        message: 'Spec subtitle must be a string if provided.',
      })
    } else if (candidate.subtitle.length > CHART_MAX_SUBTITLE_CHARS) {
      errors.push({
        code: 'subtitle.tooLong',
        path: 'subtitle',
        message: `Subtitle must be at most ${CHART_MAX_SUBTITLE_CHARS} characters (got ${candidate.subtitle.length}).`,
      })
    }
  }

  const datasetKey = candidate.dataset as DatasetKey
  const datasetDef = DATASET_CATALOG[datasetKey]
  if (typeof candidate.dataset !== 'string' || !candidate.dataset.trim()) {
    errors.push({ code: 'dataset.missing', path: 'dataset', message: 'Spec must specify a dataset.' })
  } else if (!datasetDef) {
    errors.push({
      code: 'dataset.unknown',
      path: 'dataset',
      message: `Unknown or unsupported dataset: "${String(candidate.dataset)}".`,
    })
  } else if (!datasetDef.available) {
    errors.push({
      code: 'dataset.unavailable',
      path: 'dataset',
      message: datasetDef.unavailableReason ?? 'Dataset is not available.',
    })
  }

  const chartType = candidate.type as ChartType
  if (!VALID_CHART_TYPES.has(chartType)) {
    errors.push({
      code: 'type.invalid',
      path: 'type',
      message: `Invalid chart type: "${String(candidate.type)}".`,
    })
  }

  const getDatasetField = (name: string) => {
    if (!datasetDef) return undefined
    return datasetDef.fields.find((f) => f.name === name)
  }

  // Validate measure
  const validateMeasure = (measureRaw: unknown, pathPrefix: string, isRequired: boolean) => {
    if (!measureRaw || typeof measureRaw !== 'object' || Array.isArray(measureRaw)) {
      if (isRequired) {
        errors.push({
          code: (pathPrefix === 'measure' ? 'measure.missing' : 'secondMeasure.missing'),
          path: pathPrefix,
          message: `${pathPrefix} must be an object.`,
        })
      }
      return
    }

    const measure = measureRaw as Record<string, unknown>
    const isPrimary = pathPrefix === 'measure'
    const missingCode = isPrimary ? 'measure.fieldMissing' : 'secondMeasure.fieldMissing'
    const unknownCode = isPrimary ? 'measure.fieldUnknown' : 'secondMeasure.fieldUnknown'
    const kindCode = isPrimary ? 'measure.fieldKind' : 'secondMeasure.fieldKind'
    const aggCode = isPrimary ? 'measure.aggInvalid' : 'secondMeasure.aggInvalid'

    if (typeof measure.field !== 'string' || !measure.field.trim()) {
      errors.push({
        code: missingCode,
        path: `${pathPrefix}.field`,
        message: `${pathPrefix} must specify a non-empty field name.`,
      })
    } else if (measure.field.length > CHART_MAX_STRING_ID_CHARS) {
      errors.push({
        code: 'string.tooLong',
        path: `${pathPrefix}.field`,
        message: `${pathPrefix}.field exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
      })
    } else if (datasetDef) {
      const fieldDef = getDatasetField(measure.field)
      if (!fieldDef) {
        errors.push({
          code: unknownCode,
          path: `${pathPrefix}.field`,
          message: `${pathPrefix} field "${measure.field}" does not exist in dataset "${datasetKey}".`,
        })
      } else {
        const agg = measure.agg as ChartMeasureAgg
        if (agg === 'count') {
          // any field is allowed
        } else if (agg === 'distinct') {
          if (fieldDef.kind !== 'dimension' && fieldDef.kind !== 'measure') {
            errors.push({
              code: kindCode,
              path: `${pathPrefix}.field`,
              message: `${pathPrefix} with agg distinct must be dimension or measure.`,
            })
          }
        } else {
          if (fieldDef.kind !== 'measure') {
            errors.push({
              code: kindCode,
              path: `${pathPrefix}.field`,
              message: `${pathPrefix} field "${measure.field}" must be a measure field for aggregation "${String(agg)}".`,
            })
          }
        }
      }
    }

    if (!VALID_MEASURE_AGGS.has(measure.agg as ChartMeasureAgg)) {
      errors.push({
        code: aggCode,
        path: `${pathPrefix}.agg`,
        message: `Invalid aggregation "${String(measure.agg)}" for ${pathPrefix}.`,
      })
    }

    const notAllowedCode: ChartSpecErrorCode = isPrimary
      ? 'measure.questionFieldIdNotAllowed'
      : 'secondMeasure.questionFieldIdNotAllowed'
    const invalidCode: ChartSpecErrorCode = isPrimary
      ? 'measure.questionFieldIdInvalid'
      : 'secondMeasure.questionFieldIdInvalid'

    if (measure.questionFieldId !== undefined) {
      if (datasetKey !== 'forms' || measure.field !== 'numberField') {
        errors.push({
          code: notAllowedCode,
          path: `${pathPrefix}.questionFieldId`,
          message: `${pathPrefix}.questionFieldId is only allowed on dataset "forms" with field "numberField".`,
        })
      } else if (typeof measure.questionFieldId !== 'string' || !measure.questionFieldId.trim()) {
        errors.push({
          code: invalidCode,
          path: `${pathPrefix}.questionFieldId`,
          message: `${pathPrefix}.questionFieldId must be a non-empty string.`,
        })
      } else if (measure.questionFieldId.length > CHART_MAX_STRING_ID_CHARS) {
        errors.push({
          code: 'string.tooLong',
          path: `${pathPrefix}.questionFieldId`,
          message: `${pathPrefix}.questionFieldId exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
        })
      }
    }
  }

  validateMeasure(candidate.measure, 'measure', true)

  if (chartType === 'combo') {
    validateMeasure(candidate.secondMeasure, 'secondMeasure', true)
  } else if (candidate.secondMeasure != null) {
    validateMeasure(candidate.secondMeasure, 'secondMeasure', false)
  }

  // C1: If both measures use numberField, both must name the same id (or neither does)
  if (
    candidate.measure &&
    typeof candidate.measure === 'object' &&
    !Array.isArray(candidate.measure) &&
    candidate.secondMeasure &&
    typeof candidate.secondMeasure === 'object' &&
    !Array.isArray(candidate.secondMeasure)
  ) {
    const m = candidate.measure as Record<string, unknown>
    const sm = candidate.secondMeasure as Record<string, unknown>
    if (m.field === 'numberField' && sm.field === 'numberField') {
      if (m.questionFieldId !== sm.questionFieldId) {
        errors.push({
          code: 'secondMeasure.questionFieldIdMismatch',
          path: 'secondMeasure.questionFieldId',
          message: 'secondMeasure.questionFieldId must match measure.questionFieldId when both measures use numberField.',
        })
      }
    }
  }

  // Target required for gauge, progress, targetActual
  if (chartType === 'gauge' || chartType === 'progress' || chartType === 'targetActual') {
    if (!candidate.target || typeof candidate.target !== 'object' || Array.isArray(candidate.target)) {
      errors.push({
        code: 'target.missing',
        path: 'target',
        message: `Chart type "${chartType}" requires a target.`,
      })
    } else {
      const target = candidate.target as Record<string, unknown>
      if (target.kind === 'number') {
        if (typeof target.value !== 'number' || Number.isNaN(target.value)) {
          errors.push({
            code: 'target.numberInvalid',
            path: 'target.value',
            message: 'Target number must be a valid numeric value.',
          })
        }
      } else if (target.kind === 'field') {
        if (typeof target.field !== 'string' || !target.field.trim()) {
          errors.push({
            code: 'target.fieldMissing',
            path: 'target.field',
            message: 'Target field must be a non-empty string.',
          })
        } else if (target.field.length > CHART_MAX_STRING_ID_CHARS) {
          errors.push({
            code: 'string.tooLong',
            path: 'target.field',
            message: `Target field exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
          })
        } else if (datasetDef) {
          const fieldDef = getDatasetField(target.field)
          if (!fieldDef) {
            errors.push({
              code: 'target.fieldUnknown',
              path: 'target.field',
              message: `Target field "${target.field}" does not exist in dataset "${datasetKey}".`,
            })
          } else if (fieldDef.kind !== 'measure') {
            errors.push({
              code: 'target.fieldKind',
              path: 'target.field',
              message: `Target field "${target.field}" must be a measure field.`,
            })
          }
        }
      } else {
        errors.push({
          code: 'target.invalid',
          path: 'target.kind',
          message: `Target kind must be "number" or "field", got "${String(target.kind)}".`,
        })
      }
    }
  } else if (candidate.target != null && typeof candidate.target === 'object' && !Array.isArray(candidate.target)) {
    const target = candidate.target as Record<string, unknown>
    if (target.kind === 'field' && typeof target.field === 'string' && datasetDef) {
      const fieldDef = getDatasetField(target.field)
      if (!fieldDef) {
        errors.push({
          code: 'target.fieldUnknown',
          path: 'target.field',
          message: `Target field "${target.field}" does not exist in dataset "${datasetKey}".`,
        })
      } else if (fieldDef.kind !== 'measure') {
        errors.push({
          code: 'target.fieldKind',
          path: 'target.field',
          message: `Target field "${target.field}" must be a measure field.`,
        })
      }
    }
  }

  // Time required for burn
  if (chartType === 'burn') {
    if (!candidate.time || typeof candidate.time !== 'object' || Array.isArray(candidate.time)) {
      errors.push({
        code: 'time.missing',
        path: 'time',
        message: 'Chart type "burn" requires a time dimension.',
      })
    }
  }

  // Time dimension check
  if (candidate.time != null) {
    if (typeof candidate.time !== 'object' || Array.isArray(candidate.time)) {
      errors.push({
        code: 'time.invalid',
        path: 'time',
        message: 'Time dimension must be an object if specified.',
      })
    } else {
      const time = candidate.time as Record<string, unknown>
      if (typeof time.field !== 'string' || !time.field.trim()) {
        errors.push({
          code: 'time.fieldMissing',
          path: 'time.field',
          message: 'Time dimension must specify a non-empty field name.',
        })
      } else if (time.field.length > CHART_MAX_STRING_ID_CHARS) {
        errors.push({
          code: 'string.tooLong',
          path: 'time.field',
          message: `Time field exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
        })
      } else if (datasetDef) {
        const fieldDef = getDatasetField(time.field)
        if (!fieldDef) {
          errors.push({
            code: 'time.fieldUnknown',
            path: 'time.field',
            message: `Time field "${time.field}" does not exist in dataset "${datasetKey}".`,
          })
        } else if (fieldDef.kind !== 'time') {
          errors.push({
            code: 'time.fieldKind',
            path: 'time.field',
            message: `Time field "${time.field}" must be a time field (got ${fieldDef.kind}).`,
          })
        }
      }

      if (!VALID_TIME_BUCKETS.has(String(time.bucket))) {
        errors.push({
          code: 'time.bucketInvalid',
          path: 'time.bucket',
          message: `Invalid time bucket "${String(time.bucket)}". Must be day, week, month, or quarter.`,
        })
      }
    }
  }

  // Breakdown check
  if (candidate.breakdown != null) {
    if (typeof candidate.breakdown !== 'object' || Array.isArray(candidate.breakdown)) {
      errors.push({
        code: 'breakdown.invalid',
        path: 'breakdown',
        message: 'Breakdown must be an object if specified.',
      })
    } else {
      const breakdown = candidate.breakdown as Record<string, unknown>
      if (typeof breakdown.field !== 'string' || !breakdown.field.trim()) {
        errors.push({
          code: 'breakdown.fieldMissing',
          path: 'breakdown.field',
          message: 'Breakdown must specify a non-empty field name.',
        })
      } else if (breakdown.field.length > CHART_MAX_STRING_ID_CHARS) {
        errors.push({
          code: 'string.tooLong',
          path: 'breakdown.field',
          message: `Breakdown field exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
        })
      } else if (datasetDef) {
        const fieldDef = getDatasetField(breakdown.field)
        if (!fieldDef) {
          errors.push({
            code: 'breakdown.fieldUnknown',
            path: 'breakdown.field',
            message: `Breakdown field "${breakdown.field}" does not exist in dataset "${datasetKey}".`,
          })
        } else if (fieldDef.kind !== 'dimension') {
          errors.push({
            code: 'breakdown.fieldKind',
            path: 'breakdown.field',
            message: `Breakdown field "${breakdown.field}" must be a dimension (got ${fieldDef.kind}).`,
          })
        }
      }

      if (breakdown.topN != null) {
        if (
          typeof breakdown.topN !== 'number' ||
          !Number.isInteger(breakdown.topN) ||
          breakdown.topN < CHART_MIN_TOP_N ||
          breakdown.topN > CHART_MAX_TOP_N
        ) {
          errors.push({
            code: 'breakdown.topNInvalid',
            path: 'breakdown.topN',
            message: `Breakdown topN must be an integer between ${CHART_MIN_TOP_N} and ${CHART_MAX_TOP_N} (got ${String(breakdown.topN)}).`,
          })
        }
      }

      if (breakdown.questionFieldId !== undefined) {
        if (datasetKey !== 'forms' || breakdown.field !== 'questionAnswer') {
          errors.push({
            code: 'breakdown.questionFieldIdNotAllowed',
            path: 'breakdown.questionFieldId',
            message: 'questionFieldId is only allowed on dataset "forms" with field "questionAnswer".',
          })
        } else if (typeof breakdown.questionFieldId !== 'string' || !breakdown.questionFieldId.trim()) {
          errors.push({
            code: 'breakdown.questionFieldIdInvalid',
            path: 'breakdown.questionFieldId',
            message: 'questionFieldId must be a non-empty string.',
          })
        } else if (breakdown.questionFieldId.length > CHART_MAX_STRING_ID_CHARS) {
          errors.push({
            code: 'string.tooLong',
            path: 'breakdown.questionFieldId',
            message: `Breakdown questionFieldId exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
          })
        }
      }
    }
  }

  // Range check
  if (!candidate.range || typeof candidate.range !== 'object' || Array.isArray(candidate.range)) {
    errors.push({ code: 'range.missing', path: 'range', message: 'Spec must include a range object.' })
  } else {
    const range = candidate.range as Record<string, unknown>
    if (range.kind === 'relative') {
      if (typeof range.last !== 'number' || range.last <= 0 || !Number.isInteger(range.last)) {
        errors.push({
          code: 'range.relativeLastInvalid',
          path: 'range.last',
          message: 'Relative range "last" must be a positive integer.',
        })
      }
      if (!['day', 'week', 'month'].includes(String(range.unit))) {
        errors.push({
          code: 'range.relativeUnitInvalid',
          path: 'range.unit',
          message: 'Relative range "unit" must be "day", "week", or "month".',
        })
      } else if (typeof range.last === 'number') {
        if (range.unit === 'day' && range.last > CHART_MAX_RELATIVE_DAYS) {
          errors.push({
            code: 'range.relativeLastInvalid',
            path: 'range.last',
            message: `Relative days must be <= ${CHART_MAX_RELATIVE_DAYS}.`,
          })
        } else if (range.unit === 'week' && range.last > CHART_MAX_RELATIVE_WEEKS) {
          errors.push({
            code: 'range.relativeLastInvalid',
            path: 'range.last',
            message: `Relative weeks must be <= ${CHART_MAX_RELATIVE_WEEKS}.`,
          })
        } else if (range.unit === 'month' && range.last > CHART_MAX_RELATIVE_MONTHS) {
          errors.push({
            code: 'range.relativeLastInvalid',
            path: 'range.last',
            message: `Relative months must be <= ${CHART_MAX_RELATIVE_MONTHS}.`,
          })
        }
      }
    } else if (range.kind === 'absolute') {
      const isFromStr = typeof range.from === 'string' && DATE_YMD_REGEX.test(range.from)
      const isToStr = typeof range.to === 'string' && DATE_YMD_REGEX.test(range.to)
      const fromParsed = isFromStr ? Date.parse(range.from as string) : NaN
      const toParsed = isToStr ? Date.parse(range.to as string) : NaN

      if (!isFromStr || Number.isNaN(fromParsed)) {
        errors.push({
          code: 'range.absoluteFromInvalid',
          path: 'range.from',
          message: 'Absolute range "from" must be a valid YYYY-MM-DD date string.',
        })
      }
      if (!isToStr || Number.isNaN(toParsed)) {
        errors.push({
          code: 'range.absoluteToInvalid',
          path: 'range.to',
          message: 'Absolute range "to" must be a valid YYYY-MM-DD date string.',
        })
      }
      if (isFromStr && isToStr && !Number.isNaN(fromParsed) && !Number.isNaN(toParsed)) {
        if ((range.from as string) > (range.to as string)) {
          errors.push({
            code: 'range.absoluteOrderInvalid',
            path: 'range',
            message: 'Absolute range "from" date must be earlier than or equal to "to" date.',
          })
        }
      }
    } else if (range.kind !== 'all') {
      errors.push({
        code: 'range.invalid',
        path: 'range.kind',
        message: `Invalid range kind "${String(range.kind)}".`,
      })
    }
  }

  // Filters check
  if (!Array.isArray(candidate.filters)) {
    errors.push({ code: 'filters.notArray', path: 'filters', message: 'Spec filters must be an array.' })
  } else {
    if (candidate.filters.length > CHART_MAX_FILTERS) {
      errors.push({
        code: 'filters.tooMany',
        path: 'filters',
        message: `Filters count exceeds maximum of ${CHART_MAX_FILTERS}.`,
      })
    }

    candidate.filters.forEach((filter, idx) => {
      const filterPath = `filters[${idx}]`
      if (!filter || typeof filter !== 'object' || Array.isArray(filter)) {
        errors.push({
          code: 'filter.notObject',
          path: filterPath,
          message: `Filter at index ${idx} must be an object.`,
        })
        return
      }
      const f = filter as Record<string, unknown>
      if (typeof f.field !== 'string' || !f.field.trim()) {
        errors.push({
          code: 'filter.fieldMissing',
          path: `${filterPath}.field`,
          message: `Filter at index ${idx} must specify a field.`,
        })
      } else if (f.field.length > CHART_MAX_STRING_ID_CHARS) {
        errors.push({
          code: 'string.tooLong',
          path: `${filterPath}.field`,
          message: `Filter field at index ${idx} exceeds ${CHART_MAX_STRING_ID_CHARS} characters.`,
        })
      } else if (datasetDef && !getDatasetField(f.field)) {
        errors.push({
          code: 'filter.fieldUnknown',
          path: `${filterPath}.field`,
          message: `Filter field "${f.field}" does not exist in dataset "${datasetKey}".`,
        })
      }

      const opStr = String(f.op)
      if (!VALID_FILTER_OPS.has(opStr)) {
        errors.push({
          code: 'filter.opInvalid',
          path: `${filterPath}.op`,
          message: `Invalid filter operator "${opStr}" at index ${idx}.`,
        })
      } else {
        // Value shape check per operator
        if (opStr === 'is' || opStr === 'isNot') {
          if (!isScalar(f.value)) {
            errors.push({
              code: 'filter.valueInvalid',
              path: `${filterPath}.value`,
              message: `Filter ${opStr} operator requires a scalar value at index ${idx}.`,
            })
          }
        } else if (opStr === 'in') {
          if (!Array.isArray(f.value) || f.value.length === 0 || f.value.length > CHART_MAX_FILTER_IN_ITEMS || !f.value.every(isScalar)) {
            errors.push({
              code: 'filter.valueInvalid',
              path: `${filterPath}.value`,
              message: `Filter "in" requires a non-empty array of up to ${CHART_MAX_FILTER_IN_ITEMS} scalars at index ${idx}.`,
            })
          }
        } else if (opStr === 'gt' || opStr === 'lt') {
          if (!isValidIsoDateOrNumber(f.value)) {
            errors.push({
              code: 'filter.valueInvalid',
              path: `${filterPath}.value`,
              message: `Filter "${opStr}" requires a numeric or ISO date value at index ${idx}.`,
            })
          }
        } else if (opStr === 'between') {
          if (!Array.isArray(f.value) || f.value.length !== 2) {
            errors.push({
              code: 'filter.valueInvalid',
              path: `${filterPath}.value`,
              message: `Filter "between" requires a 2-item array at index ${idx}.`,
            })
          } else {
            const [v0, v1] = f.value
            if (typeof v0 === 'number' && typeof v1 === 'number') {
              if (v0 > v1) {
                errors.push({
                  code: 'filter.valueInvalid',
                  path: `${filterPath}.value`,
                  message: `Filter "between" item 0 must be <= item 1 at index ${idx}.`,
                })
              }
            } else if (typeof v0 === 'string' && typeof v1 === 'string') {
              const p0 = Date.parse(v0)
              const p1 = Date.parse(v1)
              if (Number.isNaN(p0) || Number.isNaN(p1) || p0 > p1) {
                errors.push({
                  code: 'filter.valueInvalid',
                  path: `${filterPath}.value`,
                  message: `Filter "between" date item 0 must be <= item 1 at index ${idx}.`,
                })
              }
            } else {
              errors.push({
                code: 'filter.valueInvalid',
                path: `${filterPath}.value`,
                message: `Filter "between" requires numbers or date strings at index ${idx}.`,
              })
            }
          }
        } else if (opStr === 'isEmpty') {
          if (f.value !== undefined && f.value !== null) {
            errors.push({
              code: 'filter.valueInvalid',
              path: `${filterPath}.value`,
              message: `Filter "isEmpty" requires no value at index ${idx}.`,
            })
          }
        }
      }
    })
  }

  // Style check
  if (!candidate.style || typeof candidate.style !== 'object' || Array.isArray(candidate.style)) {
    errors.push({ code: 'style.missing', path: 'style', message: 'Spec must include a style object.' })
  } else {
    const style = candidate.style as Record<string, unknown>

    for (const key of Object.keys(style)) {
      if (!ALLOWED_STYLE_KEYS.has(key)) {
        errors.push({
          code: 'style.unknownKey',
          path: `style.${key}`,
          message: `Unknown style property "${key}".`,
        })
      }
    }

    if (style.palette !== 'theme' && style.palette !== 'custom') {
      errors.push({
        code: 'style.paletteInvalid',
        path: 'style.palette',
        message: 'Style palette must be "theme" or "custom".',
      })
    }

    if (style.palette === 'custom') {
      if (!Array.isArray(style.colors) || style.colors.length === 0) {
        errors.push({
          code: 'style.colorsRequired',
          path: 'style.colors',
          message: 'Style palette "custom" requires at least one colour.',
        })
      }
    }

    if (Array.isArray(style.colors)) {
      if (style.colors.length > CHART_MAX_COLORS) {
        errors.push({
          code: 'style.colorsTooMany',
          path: 'style.colors',
          message: `Style colors exceeds maximum of ${CHART_MAX_COLORS}.`,
        })
      }
      style.colors.forEach((color, idx) => {
        if (typeof color !== 'string' || !HEX_COLOR_REGEX.test(color)) {
          errors.push({
            code: 'style.colorInvalid',
            path: `style.colors[${idx}]`,
            message: `Custom color at index ${idx} ("${String(color)}") must be a valid #rrggbb hex string.`,
          })
        }
      })
    }

    if (style.seriesColor && typeof style.seriesColor === 'object' && !Array.isArray(style.seriesColor)) {
      const keys = Object.keys(style.seriesColor)
      if (keys.length > CHART_MAX_SERIES_COLOR_KEYS) {
        errors.push({
          code: 'style.seriesColorTooMany',
          path: 'style.seriesColor',
          message: `Style seriesColor exceeds maximum of ${CHART_MAX_SERIES_COLOR_KEYS} keys.`,
        })
      }
      Object.entries(style.seriesColor as Record<string, unknown>).forEach(([key, color]) => {
        if (typeof color !== 'string' || !HEX_COLOR_REGEX.test(color)) {
          errors.push({
            code: 'style.seriesColorInvalid',
            path: `style.seriesColor.${key}`,
            message: `seriesColor for "${key}" ("${String(color)}") must be a valid #rrggbb hex string.`,
          })
        }
      })
    }

    if (style.legend != null && !VALID_LEGENDS.has(String(style.legend))) {
      errors.push({
        code: 'style.legendInvalid',
        path: 'style.legend',
        message: 'Style legend must be one of: auto, top, right, bottom, off.',
      })
    }

    if (style.dataLabels != null && typeof style.dataLabels !== 'boolean') {
      errors.push({
        code: 'style.dataLabelsInvalid',
        path: 'style.dataLabels',
        message: 'Style dataLabels must be a boolean.',
      })
    }

    if (style.gridlines != null && typeof style.gridlines !== 'boolean') {
      errors.push({
        code: 'style.gridlinesInvalid',
        path: 'style.gridlines',
        message: 'Style gridlines must be a boolean.',
      })
    }

    if (style.axisLabels && typeof style.axisLabels === 'object' && !Array.isArray(style.axisLabels)) {
      const axis = style.axisLabels as Record<string, unknown>
      for (const axisKey of ['x', 'y', 'y2'] as const) {
        const val = axis[axisKey]
        if (val != null) {
          if (typeof val !== 'string' || val.length > CHART_MAX_AXIS_LABEL_CHARS) {
            errors.push({
              code: 'style.axisLabelsTooLong',
              path: `style.axisLabels.${axisKey}`,
              message: `Axis label "${axisKey}" exceeds ${CHART_MAX_AXIS_LABEL_CHARS} characters.`,
            })
          }
        }
      }
    }

    if (style.sort != null && !VALID_SORTS.has(String(style.sort))) {
      errors.push({
        code: 'style.sortInvalid',
        path: 'style.sort',
        message: 'Style sort must be one of: value-desc, value-asc, label.',
      })
    }

    if (style.numberFormat != null && !VALID_NUMBER_FORMATS.has(String(style.numberFormat))) {
      errors.push({
        code: 'style.numberFormatInvalid',
        path: 'style.numberFormat',
        message: 'Style numberFormat must be one of: auto, integer, percent, currency, duration.',
      })
    }
  }

  return errors
}

export function isChartSpec(input: unknown): input is ChartSpec {
  return collectChartSpecErrors(input).length === 0
}

export function chartSpecSchema(input: unknown): ValidationResult {
  const errors = collectChartSpecErrors(input)
  if (errors.length > 0) {
    return { ok: false, errors }
  }

  if (!isChartSpec(input)) {
    return {
      ok: false,
      errors: [{ code: 'spec.invalid', path: '', message: 'Invalid spec structure.' }],
    }
  }

  return { ok: true, spec: input }
}

export const validateChartSpec = chartSpecSchema
